/**
 * CROSS-TAB CHANGE NOTIFICATION — the pure half.
 *
 * ── THE PROBLEM ───────────────────────────────────────────────────────────────
 * `attachSqliteChangeFeed` emits into an IN-TAB event bus
 * (`emitManualDatabaseChange`), which is what drives React Query invalidation.
 * With the leader proxy there is exactly ONE connection process-wide, so a write
 * issued in Tab A executes on the leader — and Tab B's bus hears nothing. Tab B's
 * queries are still CORRECT if re-run; nothing ever tells them to re-run. The UI
 * goes silently stale, which is the gap between "multi-tab doesn't error" and
 * "multi-tab works".
 *
 * Two sources of change need fanning out, and the leader sees BOTH:
 *   1. Writes issued by any tab — they all execute on the leader's connection.
 *   2. Rows applied by the leader's own `pull()`. Already a known single-tab gap:
 *      pull-applied changes fire no hook at all, which is why
 *      `use-actor-turso-remote-sync.ts` synthesizes one event per actor table
 *      locally. In multi-tab only the LEADER tab runs that hook's pull.
 *
 * ── WHY THIS FILE IS PURE ─────────────────────────────────────────────────────
 * Everything here is a total function over data: no `BroadcastChannel`, no
 * `Worker`, no OPFS. That is deliberate — the multi-tab mechanism itself is
 * untestable in Jest, but the decisions that can silently corrupt behaviour
 * (which statements produce events, which events a tab must IGNORE) are testable,
 * and they live here.
 *
 * ── THE EVENT SHAPE IS NOT NEW ────────────────────────────────────────────────
 * The payload deliberately mirrors `ManualDatabaseChangeEvent`
 * (`databaseFilePath` / `databaseName` / `rowId` / `tableName`) field-for-field,
 * because a received event is fed STRAIGHT into `emitManualDatabaseChange`. This
 * is not a parallel notification system; it is the existing one, with a transport
 * in front of it.
 */
import { extractMutatedTable, splitSqlStatements } from "@-/com.npmjs.expo-sqlite";
import { isAnyObject } from "@-/com.npmjs.is-what";

/** `LeaderResourceEvent.kind` for a table-level change. */
export const TABLE_CHANGED_EVENT_KIND = "table-changed";

/**
 * `databaseName` stamped on an event that came from a REMOTE PULL rather than a
 * local statement.
 *
 * Load-bearing, not cosmetic. `use-actor-turso-remote-sync.ts`'s auto-push
 * listener ignores any change event carrying this name, because those rows came
 * FROM the remote — pushing and notifying on them would loop the user's devices
 * against each other forever. The value MUST stay identical to
 * `REMOTE_SYNC_PULL_DATABASE_NAME` there, so a pull fanned out to another tab is
 * suppressed by that tab's auto-push exactly as a local pull already is.
 */
export const REMOTE_PULL_DATABASE_NAME = "actor-remote-sync-pull";

/** `databaseName` for a change produced by a statement, matching the local feed's `"main"`. */
const STATEMENT_DATABASE_NAME = "main";

/**
 * The payload of a `table-changed` event. Structured-cloneable by construction
 * (strings and numbers only) — it crosses a `BroadcastChannel`.
 */
export interface TableChangedPayload {
  /** Mirrors `ManualDatabaseChangeEvent.databaseFilePath` (e.g. `"actor.db"`). */
  databaseFilePath: string;
  /** Mirrors `ManualDatabaseChangeEvent.databaseName`. See `REMOTE_PULL_DATABASE_NAME`. */
  databaseName: string;
  /**
   * The tab whose RPC caused this change, or `null` when the leader itself caused
   * it (a `pull()` applying remote rows, which belongs to no tab).
   *
   * The ONLY reason this field exists: the originating tab already emitted this
   * change locally — `attachSqliteChangeFeed` wraps the PROXY, so it sees the SQL
   * and fires before the RPC even returns — and must not fire again on the echo.
   * A `null` origin is nobody's echo, so every tab applies it.
   */
  originTabId: string | null;
  /** Mirrors `ManualDatabaseChangeEvent.tableName`. */
  tableName: string;
}

/**
 * Build the `table-changed` payloads for one statement the LEADER just executed.
 *
 * Mirrors `attach-sqlite-change-feed.ts`'s emission rules exactly, and reuses the
 * SAME `extractMutatedTable` / `splitSqlStatements` it does, rather than parsing
 * SQL a second way:
 *   - Mutations are detected from the SQL, never from the method name — drizzle
 *     runs `INSERT … RETURNING` through `getAllAsync`, so every query method has
 *     to be sniffed.
 *   - `begin` / `commit` / savepoint framing does not match the mutating-verb
 *     regex, so transaction control emits nothing.
 *   - A multi-statement `execAsync` script is split and each statement sniffed.
 *
 * `rowId` is deliberately absent from the payload and re-materialized as `0` on
 * receipt: the local feed only ever carries a real `lastInsertRowId` for the
 * inserting connection, and every consumer of the manual feed invalidates by
 * TABLE. Shipping a row id across tabs would imply a precision the feed does not
 * have.
 */
export function buildStatementChangePayloads(options: {
  databaseFilePath: string;
  isScript: boolean;
  originTabId: string | null;
  sql: string;
}): TableChangedPayload[] {
  const statements = options.isScript ? splitSqlStatements(options.sql) : [options.sql];
  const payloads: TableChangedPayload[] = [];
  for (const statement of statements) {
    const tableName = extractMutatedTable(statement);
    if (tableName === null) {
      continue;
    }
    payloads.push({
      databaseFilePath: options.databaseFilePath,
      databaseName: STATEMENT_DATABASE_NAME,
      originTabId: options.originTabId,
      tableName,
    });
  }
  return payloads;
}

/**
 * Build the payloads for a `pull()` that APPLIED remote rows.
 *
 * The leader cannot know WHICH tables a pull touched — sync-wasm applies remote
 * mutations below the statement layer and reports only "changes were applied" as
 * a boolean. So this fans out one event per known table, exactly as the
 * single-tab path already does (`emitActorTablesChangedByRemotePull`), and
 * carries `REMOTE_PULL_DATABASE_NAME` so the receiving tab's auto-push ignores it.
 *
 * `originTabId` is always `null`: a pull belongs to the leader, not to any tab's
 * call, so nobody suppresses it — including the leader's own tab, whose local
 * `pull()` RPC produced no statement-level events either.
 *
 * A pull that applied NOTHING must produce no payloads. Emitting on an empty pull
 * would invalidate every actor query on every idle poll of the drain loop, in
 * every tab at once.
 */
export function buildPullChangePayloads(options: {
  appliedChanges: boolean;
  databaseFilePath: string;
  tableNames: readonly string[];
}): TableChangedPayload[] {
  if (!options.appliedChanges) {
    return [];
  }
  return options.tableNames.map((tableName) => ({
    databaseFilePath: options.databaseFilePath,
    databaseName: REMOTE_PULL_DATABASE_NAME,
    originTabId: null,
    tableName,
  }));
}

/** Narrow an event payload that arrived over `postMessage`, or `null`. */
export function parseTableChangedPayload(value: unknown): TableChangedPayload | null {
  if (!isAnyObject(value)) {
    return null;
  }
  const databaseFilePath: unknown = Reflect.get(value, "databaseFilePath");
  const databaseName: unknown = Reflect.get(value, "databaseName");
  const originTabId: unknown = Reflect.get(value, "originTabId");
  const tableName: unknown = Reflect.get(value, "tableName");
  if (
    typeof databaseFilePath !== "string" ||
    typeof databaseName !== "string" ||
    typeof tableName !== "string" ||
    (originTabId !== null && typeof originTabId !== "string")
  ) {
    return null;
  }
  return { databaseFilePath, databaseName, originTabId, tableName };
}

/**
 * ORIGIN-TAB DEDUPE. Should the tab identified by `receivingTabId` re-emit this
 * broadcast onto its local change bus?
 *
 * No, when the payload's origin IS this tab: `attachSqliteChangeFeed` wraps the
 * PROXY connection, so the issuing tab already emitted this exact change locally,
 * synchronously at statement completion, before the RPC returned. Re-emitting the
 * echo would double-fire every invalidation in the tab that did the work.
 *
 * (`emitManualDatabaseChange`'s consumers are invalidation-shaped and idempotent,
 * so a duplicate would not be *incorrect* — but the actor auto-push debounce is
 * driven by this same feed, so a doubled event is doubled work on the sync path,
 * and "harmless because idempotent" is exactly the reasoning that decays. Dedupe
 * explicitly instead of relying on it.)
 *
 * Yes, in every other case — including a `null` origin (a `pull()`, which belongs
 * to no tab and which NO tab has heard about locally).
 */
export function shouldApplyTableChangedPayload(
  payload: TableChangedPayload,
  receivingTabId: string,
): boolean {
  return payload.originTabId !== receivingTabId;
}
